import { Enemy, EnemyState } from '../entities/Enemy';

const MAX_ACTIVE_CHASERS = 2;

/**
 * EnemyManager orchestrates enemy groups so that at most MAX_ACTIVE_CHASERS
 * enemies chase the player simultaneously. Others hold position until a slot
 * opens (an active chaser dies or moves out of range).
 *
 * Slot lifecycle:
 *   HOLD  → enemy is in range but has no slot yet; grant one if space available.
 *   CHASE / SEEK_COVER → enemy actively chasing or in cover; keep slot.
 *   PATROL → enemy out of range; revoke slot.
 *   DEAD  → remove from accounting.
 */
export class EnemyManager {
  private enemies: Enemy[] = [];

  addEnemy(enemy: Enemy): void {
    this.enemies.push(enemy);
  }

  /**
   * Called each frame (before individual Enemy.update calls) to assign or
   * revoke chase-slot permission.
   */
  update(): void {
    const live = this.enemies.filter((e) => !e.isDead());

    // First pass: revoke slots from enemies that returned to patrol (out of range)
    for (const enemy of live) {
      if (enemy.isActiveChaser && enemy.state === EnemyState.PATROL) {
        enemy.isActiveChaser = false;
      }
    }

    // Second pass: grant slots to enemies waiting in HOLD state
    let activeCount = live.filter((e) => e.isActiveChaser).length;
    for (const enemy of live) {
      if (!enemy.isActiveChaser && enemy.state === EnemyState.HOLD) {
        if (activeCount < MAX_ACTIVE_CHASERS) {
          enemy.isActiveChaser = true;
          activeCount++;
        }
      }
    }
  }

  getEnemies(): Enemy[] {
    return this.enemies;
  }
}
